import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatInstantWithZone } from '@/lib/format';
import { TYPED_SIGNATURE_CLASS } from '@/lib/signaturePad';
import { cn } from '@/lib/utils';
import {
  useEstimateSignature,
  type Estimate,
  type EstimateCustomerAnswer,
} from '@/routes/estimates/useEstimates';

/**
 * The customer's answer, as the office reads it (M2-08, pass B), from the
 * detail's `customerAnswer`: who signed, when and how, the signature itself,
 * the consent they ticked in the words they saw, and a decline's comment.
 *
 * - **Told apart from the office's own record.** `customerAnswer` is null when
 *   someone in the office recorded the answer (M2-05's path), and this says so
 *   in as many words — there is no signature to look for.
 * - **The device only when the API sends it.** `clientAddress` and `userAgent`
 *   are null for a caller without `customers.read` — a technician on the job —
 *   so their rows are left out, not shown empty (Kyle, 2026-09-29).
 * - **`contentMatches` false is a warning**, not a footnote: the revision's
 *   lines or totals no longer hash to what was signed.
 * - **The time is on the SHOP's clock** with the zone named, like every other
 *   instant on this page — one formatter for the date and the time.
 */
export function CustomerAnswer({
  estimate,
  timeZone,
}: {
  estimate: Estimate;
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();
  const answer = estimate.customerAnswer;
  const officeRecorded =
    answer === null && (estimate.approvedAt !== null || estimate.declinedAt !== null);
  if (answer === null && !officeRecorded) return null;

  return (
    <section
      aria-labelledby="customer-answer-heading"
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
      data-testid="customer-answer"
    >
      <h2 id="customer-answer-heading" className="text-base font-semibold">
        {t('estimates.answer.heading')}
      </h2>
      {answer === null ? (
        <p className="text-sm" data-testid="answer-source">
          {estimate.approvedAt !== null
            ? t('estimates.answer.officeApproved')
            : t('estimates.answer.officeDeclined')}
        </p>
      ) : (
        <SignedRecord estimate={estimate} answer={answer} timeZone={timeZone} />
      )}
    </section>
  );
}

function SignedRecord({
  estimate,
  answer,
  timeZone,
}: {
  estimate: Estimate;
  answer: EstimateCustomerAnswer;
  timeZone: string | undefined;
}) {
  const { t } = useTranslation();
  const approved = answer.outcome === 'Approved';
  const when = formatInstantWithZone(answer.answeredAt, timeZone);

  return (
    <>
      <p className="text-sm font-medium" data-testid="answer-source">
        {approved ? t('estimates.answer.customerApproved') : t('estimates.answer.customerDeclined')}
      </p>

      {approved && !answer.contentMatches ? (
        <p
          className="rounded-md border border-[var(--color-destructive)] px-3 py-2 text-sm font-medium text-[var(--color-destructive)]"
          data-testid="content-mismatch"
        >
          {t('estimates.answer.contentMismatch')}
        </p>
      ) : null}

      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        {approved ? (
          <Row label={t('estimates.answer.signedBy')} testId="answer-signer">
            {answer.signerName}
          </Row>
        ) : null}
        <Row
          label={approved ? t('estimates.answer.signedAt') : t('estimates.answer.declinedAt')}
          testId="answer-when"
        >
          {when}
        </Row>
        {approved ? (
          <Row label={t('estimates.answer.how')} testId="answer-how">
            {answer.signatureKind === 'Drawn'
              ? t('estimates.answer.drawn')
              : t('estimates.answer.typed')}
          </Row>
        ) : null}
        {approved ? (
          <Row label={t('estimates.answer.signature')} testId="answer-signature">
            {answer.signatureKind === 'Drawn' && answer.hasSignatureImage ? (
              <SignatureImage estimateId={estimate.id} signerName={answer.signerName ?? ''} />
            ) : (
              <span className={cn(TYPED_SIGNATURE_CLASS, 'break-words')}>
                {answer.typedSignature}
              </span>
            )}
          </Row>
        ) : null}
        {approved && answer.consentText ? (
          <Row label={t('estimates.answer.consent')} testId="answer-consent">
            {t('estimates.answer.consentQuoted', {
              text: answer.consentText,
              version: answer.consentVersion,
            })}
          </Row>
        ) : null}
        {!approved ? (
          <Row label={t('estimates.answer.comment')} testId="answer-comment">
            {answer.comment ? (
              <span className="whitespace-pre-line">{answer.comment}</span>
            ) : (
              <span className="text-[var(--color-muted-foreground)]">
                {t('estimates.answer.noComment')}
              </span>
            )}
          </Row>
        ) : null}
        {answer.clientAddress !== null ? (
          <Row label={t('estimates.answer.clientAddress')} testId="answer-address">
            {answer.clientAddress}
          </Row>
        ) : null}
        {answer.userAgent !== null ? (
          <Row label={t('estimates.answer.userAgent')} testId="answer-agent">
            <span className="break-all">{answer.userAgent}</span>
          </Row>
        ) : null}
      </dl>
    </>
  );
}

function Row({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="font-medium">{label}</dt>
      <dd className="min-w-0" data-testid={testId}>
        {children}
      </dd>
    </>
  );
}

/**
 * The drawn signature. ⚠️ Fetched as a blob with the bearer token and shown
 * through an object URL — an `<img src>` pointing at the API would go without
 * the token and be refused. The URL is revoked when the image leaves the page.
 * Shown on white whatever the theme: the ink is near-black on transparent.
 */
function SignatureImage({ estimateId, signerName }: { estimateId: string; signerName: string }) {
  const { t } = useTranslation();
  const signature = useEstimateSignature(estimateId, true);
  const [url, setUrl] = useState<string | null>(null);
  const blob = signature.data;

  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [blob]);

  if (signature.isError) {
    return (
      <span className="flex flex-wrap items-center gap-2" role="alert">
        {t('estimates.answer.signatureFailed')}
        <Button type="button" variant="outline" size="sm" onClick={() => void signature.refetch()}>
          {t('states.tryAgain')}
        </Button>
      </span>
    );
  }
  if (!url) {
    return (
      <span className="text-[var(--color-muted-foreground)]">
        {t('estimates.answer.signatureLoading')}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt={t('estimates.answer.signatureAlt', { name: signerName })}
      className="max-h-32 w-full max-w-sm rounded-md border border-[var(--color-border)] bg-white object-contain"
      data-testid="signature-image"
    />
  );
}
